import { migrateEntity } from '@sites/entity-migrator';

import { themeRegistry } from './register';
import type { WebsiteThemeProps } from './schemas';

/**
 * Reading and writing `draft_theme_settings` — the two ends of the only place a theme is persisted.
 *
 * Four call sites parsed that blob with a bare `JSON.parse` before this existed: the editor, the
 * mini-landing app, and two branches of the SSR content strategy. Four readers of an unvalidated
 * shape is how the shape drifts; one reader that migrates is how it stops.
 */

/**
 * What a caller gets back: the theme WITHOUT the entity tag.
 *
 * The tag is deliberately not handed to the apps. Their state, reducers and settings panels all work
 * on `{ colors, fonts, buttons, root }`, and adding three keys to the object they pass around would
 * make this a behaviour change on top of a migration — the same reasoning that kept the deprecated
 * button fields in v0. The version is stamped at WRITE time instead, by `serializeThemeSettings`, so
 * the round trip still leaves a versioned blob on the page.
 */
export type ThemeSettingsRead = WebsiteThemeProps;

/**
 * Parses and migrates a stored theme, or returns `{}` when there is nothing usable.
 *
 * Hands back `props` — the payload — rather than the whole entity. The tag and version are
 * bookkeeping the editor's state, its settings panels and the SSR renderer never touch, and putting
 * three extra keys into the object they pass around would make this a behaviour change on top of a
 * migration. The version is stamped at WRITE time instead, so the round trip still leaves a
 * versioned blob on the page.
 *
 * Returning an empty theme rather than throwing is what the call sites already did with `?? {}` —
 * and it is the right failure: a site with no palette renders with browser defaults, while an
 * exception here would take down a whole page load over a field that only affects how it looks.
 */
export function readThemeSettings(raw: string | null | undefined): ThemeSettingsRead {
    return readThemeSettingsResult(raw).theme;
}

/** The three independent parts a theme is built from — see `readThemeSettingsResult`. */
const THEME_PARTS = ['colors', 'fonts', 'buttons'] as const;

type ThemePart = (typeof THEME_PARTS)[number];

/**
 * How much of the stored theme could be read.
 *
 * `ok` — all of it. `salvaged` — some of it; `dropped` names what could not be read.
 * `empty` — none of it, or there was nothing stored.
 */
export type ThemeReadStatus = 'ok' | 'salvaged' | 'empty';

export type ThemeReadOutcome = {
    theme: ThemeSettingsRead;
    status: ThemeReadStatus;
    /** Paths that could not be read, e.g. `fonts[3]` or `buttons`. Empty unless `salvaged`. */
    dropped: string[];
};

/**
 * Rebuilds the blob around ONE part, keeping whatever envelope it arrived in.
 *
 * A pre-versioning theme is `{ colors, fonts, buttons }` at the root; a versioned one carries them
 * under `props` beside the tag `themeRegistry` dispatches on. Probing has to preserve that or the
 * registry sends a v0 blob down the legacy path (or refuses it outright), and every probe fails for
 * a reason that has nothing to do with the part being probed.
 */
function withOnlyPart(parsed: Record<string, unknown>, part: ThemePart, value: unknown): unknown {
    const props = parsed['props'];

    if (props && typeof props === 'object') {
        return { ...parsed, props: { [part]: value } };
    }

    return { ...parsed, colors: undefined, fonts: undefined, buttons: undefined, [part]: value };
}

/** One probe: does this part, alone, survive the migration? */
function readPart(parsed: Record<string, unknown>, part: ThemePart, value: unknown): unknown {
    const migrated = migrateEntity(withOnlyPart(parsed, part, value), themeRegistry);

    return migrated.type === 'ok' ? (migrated.value.props as Record<string, unknown>)[part] : undefined;
}

/**
 * Reads as much of the theme as validates, part by part and then entry by entry.
 *
 * **Why this exists.** The schemas are strict where the data is a closed vocabulary — a font family,
 * a colour slot, a button style — and a strict schema fails the WHOLE parse for one bad value. The
 * blast radius was the entire theme: a single unrecognised `font` cost the palette and the buttons
 * too, and the reader answered `{}`. Every consumer then rendered browser defaults, and — much
 * worse — the editor holds that `{}` and its next autosave writes it back, so one odd font value
 * permanently destroyed a site's whole design. The tolerance below is not politeness; it is the
 * difference between losing a font and losing a theme.
 *
 * **Three passes, cheapest first.** The whole blob (what almost every page takes, and the only path
 * that costs anything); then each part on its own, so a broken font set cannot take the palette with
 * it; then each ENTRY of a broken part, so one unreadable tag costs one tag. Nothing is repaired or
 * guessed — an entry either validates against the real schema or is reported in `dropped`.
 *
 * **Salvage is still lossy, and callers that WRITE must treat it as such.** A salvaged theme
 * serialized back drops what could not be read, this time for good. That is why the status is
 * exposed rather than kept private: `apps/mcp-backend` refuses to rewrite a theme it could only
 * partly read, and names what it could not read.
 */
export function readThemeSettingsResult(raw: string | null | undefined): ThemeReadOutcome {
    const empty: ThemeReadOutcome = { theme: {}, status: 'empty', dropped: [] };

    if (!raw) {
        return empty;
    }

    let parsed: unknown;

    try {
        parsed = JSON.parse(raw);
    } catch {
        return empty;
    }

    const migrated = migrateEntity(parsed, themeRegistry);

    if (migrated.type === 'ok') {
        return { theme: migrated.value.props, status: 'ok', dropped: [] };
    }

    if (typeof parsed !== 'object' || parsed === null) {
        return empty;
    }

    const blob = parsed as Record<string, unknown>;
    const stored = ((blob['props'] as Record<string, unknown> | undefined) ?? blob) as Record<string, unknown>;
    const theme: Record<string, unknown> = {};
    const dropped: string[] = [];

    for (const part of THEME_PARTS) {
        const value = stored[part];

        if (value === undefined || value === null) {
            continue;
        }

        const whole = readPart(blob, part, value);

        if (whole !== undefined) {
            theme[part] = whole;
            continue;
        }

        const selected = (value as { selected?: unknown }).selected;

        if (!Array.isArray(selected)) {
            dropped.push(part);
            continue;
        }

        // Entry by entry, each probed inside its own part so `selectedIndex` and `syncWithDesktop`
        // travel with it — they are read off the first entry that survives, which is also the only
        // way to keep them when the array they belong to is being rebuilt.
        let head: Record<string, unknown> | undefined;
        const kept: unknown[] = [];

        selected.forEach((entry, index) => {
            const one = readPart(blob, part, { ...(value as object), selected: [entry] }) as
                | { selected?: unknown[] }
                | undefined;

            if (one?.selected?.length) {
                head = head ?? (one as Record<string, unknown>);
                kept.push(one.selected[0]);
                return;
            }

            dropped.push(`${part}[${index}]`);
        });

        if (kept.length) {
            theme[part] = { ...head, selected: kept };
        }
    }

    if (!Object.keys(theme).length) {
        // Nothing survived. Reported as `empty` rather than `salvaged`: there is no partial theme to
        // render, and a caller deciding whether it may write back needs the two told apart.
        return { theme: {}, status: 'empty', dropped };
    }

    return { theme: theme as ThemeSettingsRead, status: 'salvaged', dropped };
}

/**
 * Wraps the payload back into a tagged entity and serializes it — the write half, and the only place
 * a version reaches the page.
 *
 * A theme saved through here is read back on the versioned path next time instead of the legacy one,
 * so the migration's job shrinks with every save rather than being paid forever.
 *
 * Typed as a plain object rather than as the validated shape, deliberately: the write path must not
 * be a validation gate. Whatever the editor currently holds is the user's theme, and refusing to
 * save it because a field has a shape this package has not pinned down would lose their work to
 * protect a schema. Reading validates; writing stamps.
 */
export function serializeThemeSettings(props: object): string {
    return JSON.stringify({ entityType: 'theme', entitySubType: 'website', version: 0, props });
}
